import { useEffect, useRef } from 'react'
import '../../input.css'
import { useTranslation } from 'react-i18next'
import Seo from '../Seo'
import HeroSection from './HeroSection'
import FoundationSection from './FoundationSection'
import CustomerLogosSection from './CustomerLogosSection'
import CtaClose from '../common/CtaClose'
import { getHomeContent } from '../../content/homeContent'
import trackEvent from '../../utils/trackEvent'
import SiteAmbient from '../common/SiteAmbient'

const ACCENT = {
  1: { bar: 'bg-pyramid-1', text: 'text-pyramid-1' },
  10: { bar: 'bg-pyramid-10', text: 'text-pyramid-10' },
  19: { bar: 'bg-pyramid-19', text: 'text-pyramid-19' },
}

// Scroll-triggered reveal hook
function useReveal(threshold = 0.12) {
  const ref = useRef(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    if (typeof IntersectionObserver === 'undefined') {
      el.classList.add('is-visible')
      return undefined
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-visible')
          obs.disconnect()
        }
      },
      { threshold }
    )
    obs.observe(el)
    // failsafe: if the observer never fires (hidden tab, odd engine states),
    // the section still reveals instead of holding opacity 0 forever
    const failsafe = setTimeout(() => { el.classList.add('is-visible'); obs.disconnect() }, 2600)
    return () => { obs.disconnect(); clearTimeout(failsafe) }
  }, [threshold])
  return ref
}

const HomePage = () => {
  const { i18n } = useTranslation()
  const c = getHomeContent(i18n.language)

  const pathsRef = useReveal(0.08)
  const portfolioRef = useReveal(0.08)
  const methodRef = useReveal(0.08)
  const firmRef = useReveal(0.08)
  const trackRef = useReveal(0.08)

  return (
    <div className="relative isolate flex h-full w-full flex-col bg-transparent font-Quicksand text-white">
      <SiteAmbient />
      <Seo
        title={c.seo.title}
        description={c.seo.description}
        path="/"
        image="/images/og-leanit101-v2.jpg"
      />

      <HeroSection />

      {/* ── Proof, straight after the pitch ──────────────────────────────── */}
      <CustomerLogosSection />

      {/* ── The portfolio, in six shapes ─────────────────────────────────── */}
      <section className="relative px-6 py-24 md:px-16">
        <div ref={portfolioRef} className="reveal-on-scroll mx-auto max-w-6xl">
          <p className="text-xs uppercase tracking-[0.4em] text-pyramid-1/75">{c.portfolio.kicker}</p>
          <h2 className="mt-4 max-w-xl text-3xl font-light leading-tight md:text-4xl">{c.portfolio.title}</h2>
          <p className="mt-4 max-w-2xl leading-8 text-slate-300">{c.portfolio.body}</p>
          <div className="stagger-grid mt-10 grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {c.portfolio.groups.map((g) => (
              <a
                key={g.id}
                href={`/services#${g.id}`}
                onClick={() => trackEvent(`home_portfolio_${g.id}`)}
                className="stagger-card group bg-[#151b23] p-7 transition-colors hover:bg-[#1a212a]"
              >
                <span className={`block h-1 w-8 rounded-full ${ACCENT[g.accent].bar}`} />
                <p className="mt-4 text-lg font-semibold text-white">{g.name}</p>
                <p className="mt-2 text-sm leading-6 text-slate-400">{g.line}</p>
                <span className={`mt-4 inline-block text-sm font-semibold ${ACCENT[g.accent].text} opacity-0 transition-opacity group-hover:opacity-100`} aria-hidden="true">&#8594;</span>
              </a>
            ))}
          </div>
          <a href="/services" onClick={() => trackEvent('home_portfolio_all')} className="btn-board btn-board-lime mt-8">
            {c.portfolio.cta}
            <span aria-hidden="true">&#8594;</span>
          </a>
        </div>
      </section>


      {/* ── Method: the Maestro board, honestly ──────────────────────────── */}
      <section className="relative px-6 py-24 md:px-16">
        <div ref={methodRef} className="reveal-on-scroll mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.4em] text-pyramid-19/80">{c.method.kicker}</p>
            <h2 className="mt-4 text-3xl font-light leading-tight md:text-4xl">{c.method.title}</h2>
            <p className="mt-4 leading-8 text-slate-300">{c.method.body}</p>
            <a href="/maestro" onClick={() => trackEvent('home_method_click')} className="btn-board btn-board-cyan mt-6">
              {c.method.cta}
              <span aria-hidden="true">&#8594;</span>
            </a>
          </div>
          <div className="min-w-0 lg:col-span-7">
            <div className="max-w-full overflow-x-auto">
              <div className="overflow-hidden rounded-xl bg-[#1a212a] ring-1 ring-white/[0.12] shadow-2xl shadow-black/50">
                <img src="/images/maestro/metrics-project.jpg" alt={c.method.shotAlt} loading="lazy" className="block w-full" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Two paths: Forward Deployed Engineering + Benjamin ───────────── */}
      <section className="relative bg-transparent px-6 py-24 md:px-16 overflow-hidden">
        {/* warm pyramid glow */}
        <div
          className="absolute top-1/4 right-0 h-80 w-80 rounded-full opacity-10 pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(201,211,58,0.5) 0%, transparent 70%)',
            filter: 'blur(70px)',
            transform: 'translate(30%, -30%)',
          }}
        />

        <div className="relative z-10 mx-auto max-w-6xl">
          <div ref={pathsRef} className="reveal-on-scroll max-w-2xl">
            <p className="text-xs uppercase tracking-[0.4em] text-pyramid-1/75">{c.paths.lead}</p>
          </div>

          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            {/* Maestro — run the system yourself, no consulting required */}
            <div className="flex flex-col rounded-2xl border border-pyramid-19/25 bg-white/[0.02] p-8 transition-colors hover:border-pyramid-19/45 lg:p-10">
              <p className="text-xs uppercase tracking-[0.3em] text-pyramid-19/80">{c.paths.maestro.label}</p>
              <h3 className="mt-4 text-2xl font-light leading-tight text-white md:text-[2rem]">
                {c.paths.maestro.headline}
              </h3>
              <p className="mt-4 leading-7 text-slate-300">{c.paths.maestro.body}</p>
              <ul className="mt-6 space-y-3">
                {c.paths.maestro.points.map((point) => (
                  <li key={point} className="flex items-start gap-3 text-sm leading-6 text-slate-300">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-pyramid-19" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-8">
                <a
                  href="/maestro"
                  onClick={() => trackEvent('home_paths_maestro_click')}
                  className="inline-flex items-center gap-2 rounded-[10px] bg-gradient-to-r from-pyramid-1 via-pyramid-10 to-pyramid-19 px-6 py-3 font-semibold text-ebony shadow-lg shadow-pyramid-10/30 transition-all hover:-translate-y-0.5 hover:shadow-pyramid-10/50"
                >
                  {c.paths.maestro.cta}
                  <span aria-hidden="true">&#8594;</span>
                </a>
              </div>
            </div>

            {/* Forward Deployed Engineering — bring us in */}
            <div className="flex flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-8 transition-colors hover:border-white/20 lg:p-10">
              <p className="text-xs uppercase tracking-[0.3em] text-pyramid-1/70">{c.build.label}</p>
              <h3 className="mt-4 text-2xl font-light leading-tight text-white md:text-[2rem]">
                {c.build.headline}
              </h3>
              <p className="mt-4 leading-7 text-slate-300">{c.build.body}</p>
              <ul className="mt-6 space-y-3">
                {c.build.points.map((point) => (
                  <li key={point} className="flex items-start gap-3 text-sm leading-6 text-slate-300">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-pyramid-1" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-8">
                <a
                  href="/forward-deployed-engineering"
                  onClick={() => trackEvent('home_fde_click')}
                  className="inline-flex items-center gap-2 rounded-[10px] border border-white/20 bg-white/5 px-6 py-3 font-semibold text-white transition-all hover:-translate-y-0.5 hover:border-white/40 hover:bg-white/10"
                >
                  {c.build.cta}
                  <span aria-hidden="true">&#8594;</span>
                </a>
              </div>
            </div>
          </div>

          <p className="mt-8 max-w-3xl leading-7 text-slate-400">{c.paths.connective}</p>
        </div>
      </section>

      {/* ── Benjamin: the customer-operations domain, on its own band ────── */}
      <section className="relative px-6 py-24 md:px-16">
        <div className="mx-auto max-w-6xl rounded-2xl border border-white/10 bg-white/[0.02] p-8 lg:p-10">
          <div className="grid items-center gap-10 lg:grid-cols-[1.2fr_0.8fr]">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-slate-400">{c.benjamin.label}</p>
              <h3 className="mt-4 text-2xl font-light leading-tight text-white md:text-[2rem]">
                {c.benjamin.headline}
              </h3>
              <p className="mt-4 leading-7 text-slate-300">{c.benjamin.body}</p>
              <ul className="mt-6 space-y-3">
                {c.benjamin.points.map((point) => (
                  <li key={point} className="flex items-start gap-3 text-sm leading-6 text-slate-300">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-pyramid-19" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap items-center gap-5">
                <a
                  href="https://benjaminintelligence.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('home_benjamin_explore_click')}
                  className="inline-flex items-center gap-2 rounded-[10px] border border-white/20 bg-white/5 px-6 py-3 font-semibold text-white transition-all hover:-translate-y-0.5 hover:border-white/40 hover:bg-white/10"
                >
                  {c.benjamin.cta}
                  <span aria-hidden="true">&#8599;</span>
                </a>
                <a
                  href="https://benjaminintelligence.com/book-demo"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('home_benjamin_demo_click')}
                  className="text-sm text-slate-400 underline underline-offset-4 transition-colors hover:text-white"
                >
                  {c.benjamin.secondaryCta}
                </a>
              </div>
            </div>
            <div className="hidden items-center justify-center lg:flex">
              <img src="/images/logo-benjamin.png" alt="Benjamin · Operational Intelligence" loading="lazy" className="max-h-64 w-auto object-contain" />
            </div>
          </div>
        </div>
      </section>

      {/* ── The firm: roles, honestly sized ──────────────────────────────── */}
      <section className="relative px-6 py-24 md:px-16">
        <div ref={firmRef} className="reveal-on-scroll mx-auto max-w-6xl">
          <p className="text-xs uppercase tracking-[0.4em] text-pyramid-10/80">{c.firm.kicker}</p>
          <h2 className="mt-4 max-w-xl text-3xl font-light leading-tight md:text-4xl">{c.firm.title}</h2>
          <p className="mt-4 max-w-2xl leading-8 text-slate-300">{c.firm.body}</p>
          <div className="stagger-grid mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {c.firm.roles.map((r) => (
              <div
                key={r.name}
                className={
                  r.forming
                    ? 'stagger-card rounded-2xl border border-dashed border-white/20 bg-transparent p-7'
                    : 'stagger-card rounded-2xl border border-white/10 bg-white/[0.02] p-7'
                }
              >
                <p className="font-semibold text-white">{r.name}</p>
                <p className="mt-3 text-sm leading-6 text-slate-400">{r.line}</p>
              </div>
            ))}
          </div>
          <a href="/about" onClick={() => trackEvent('home_firm_click')} className="btn-board btn-board-orange mt-8">
            {c.firm.cta}
            <span aria-hidden="true">&#8594;</span>
          </a>
        </div>
      </section>

      {/* ── Foundation: the depth behind the work ───────────────────────── */}
      <FoundationSection content={c.company} />

      {/* ── Track record ─────────────────────────────────────────────────── */}
      <section className="relative bg-transparent px-6 py-24 md:px-16 overflow-hidden">
        {/* Background left glow */}
        <div
          className="absolute top-1/2 left-0 w-80 h-80 rounded-full opacity-8 pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(248,129,22,0.4) 0%, transparent 70%)',
            filter: 'blur(70px)',
            transform: 'translate(-40%, -50%)',
          }}
        />

        <div className="mx-auto max-w-6xl">
          <div ref={trackRef} className="reveal-on-scroll">
            <p className="text-xs uppercase tracking-[0.4em] text-pyramid-1/75">
              {c.track.eyebrow}
            </p>
            <h2 className="mt-4 max-w-xl text-3xl font-light text-white md:text-4xl leading-tight">
              {c.track.headline}
            </h2>
            <p className="mt-4 max-w-2xl text-lg leading-8 text-slate-300">
              {c.track.body}
            </p>

            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4 stagger-grid">
              {c.track.items.map((item, i) => (
                <div
                  key={item.client}
                  className="track-card stagger-card rounded-2xl border border-white/10 bg-white/[0.03] p-7 text-left"
                  style={{ '--card-delay': `${i * 130}ms` }}
                >
                  <p className="text-xs uppercase tracking-[0.38em] text-pyramid-1/70">
                    {item.client}
                  </p>
                  <p className="mt-3 text-2xl font-semibold text-white leading-tight">
                    {item.metric}
                  </p>
                  <div className="mt-3 h-px w-10 bg-gradient-to-r from-pyramid-1/50 to-transparent" />
                  <p className="mt-4 text-sm leading-6 text-slate-400">{item.body}</p>
                </div>
              ))}
            </div>

            {/* See full case studies CTA */}
            <div className="mt-10">
              <a
                href="/about"
                className="inline-flex items-center gap-2 text-sm text-pyramid-1/80 hover:text-pyramid-1 transition-colors group"
              >
                {c.track.cta}
                <span className="transition-transform group-hover:translate-x-1" aria-hidden="true">&#8594;</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <CtaClose lane="discovery" page="home" />
    </div>
  )
}

export default HomePage
